/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import axios from 'axios';
import React from 'react';
import {
  CustomIconButton,
  ToolContainerDraggable,
  useDebounce,
  useWheelStopPropagation,
} from '@opengeoweb/shared';
import type { layerTypes, uiTypes } from '@opengeoweb/store';
import { ExitDomain } from '@opengeoweb/theme';
import type { MapLocation } from '@opengeoweb/webmap-react';
import type { GFILayer, GFIResult } from './utils';
import { getLayersToUpdate } from './utils';
import { useCoreTranslation } from '../../../utils/i18n';
import { sanitizeHTML } from '../../../utils/sanitizeHTML';

interface GetFeatureInfoDialogProps {
  layers: layerTypes.Layer[];
  isOpen: boolean;
  onClose: () => void;
  onMouseDown?: () => void;
  mapId: string;
  showMapId?: boolean;
  order?: number;
  source?: uiTypes.Source;
  mapPinLocation?: MapLocation;
}

interface UpdateLayerResultsParams {
  layerId: string;
  data: string;
  isLoading: boolean;
}

const GetFeatureInfoDialog: React.FC<GetFeatureInfoDialogProps> = ({
  layers,
  isOpen,
  onClose,
  onMouseDown = (): void => {},
  mapId,
  showMapId = false,
  order = 0,
  source = 'app',
  mapPinLocation,
}: GetFeatureInfoDialogProps) => {
  const { t } = useCoreTranslation();

  /* Ref, state and update function for results per layer map */
  const gfiResultMap = React.useRef(new Map<string, GFIResult>()).current;
  // eslint-disable-next-line react/hook-use-state
  const [, triggerRerender] = React.useState<string>();
  const updateLayerResult = ({
    layerId,
    data,
    isLoading,
  }: UpdateLayerResultsParams): void => {
    gfiResultMap.set(layerId, { data, isLoading });
    triggerRerender(`${layerId}${isLoading}`);
  };

  /* Build a layerlist array with a set of arguments per layer to do the query for */
  const layerToUpdateList: GFILayer[] =
    isOpen && mapPinLocation ? getLayersToUpdate(mapPinLocation, layers) : [];
  /* 
    Build a string based on the GFI urls. If this changes for whatever reason we need to update. 
    These strings can change very often, therefore they need to be debounced and only use the most recent result  must be used.
  */
  const debouncedlayerListString = useDebounce(
    layerToUpdateList.map((layer) => layer.layerId + layer.url).join(','),
    100,
  );

  /* Do a GFI call to the server and use the updateLayerResult to provide the results. */
  const fetchLayerInfo = async (
    url: string,
    layerId: string,
  ): Promise<void> => {
    if (url?.length === 0) {
      updateLayerResult({
        layerId,
        data: t('feature_info_no_getfeatureinfo'),
        isLoading: false,
      });
      return;
    }
    try {
      updateLayerResult({ layerId, data: '', isLoading: true });

      const { data } = await axios.get(url);
      updateLayerResult({ layerId, data, isLoading: false });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'fetchLayerInfo failed';
      updateLayerResult({ layerId, data: errorMessage, isLoading: false });
    }
  };
  /* For each changed getfeature url string, load new data using WMS GetFeatureInfo */
  React.useEffect(() => {
    if (mapPinLocation && isOpen) {
      layerToUpdateList.forEach(({ url, layerId }) => {
        void fetchLayerInfo(url, layerId);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedlayerListString]);

  const hasLayers = layers && layers.length > 0;

  const stopWheelEventPropagationRef =
    useWheelStopPropagation<HTMLDivElement>();

  return (
    <ToolContainerDraggable
      bounds="parent"
      data-testid="moveable-getfeatureinfo"
      initialMaxHeight={440}
      isOpen={isOpen}
      minWidth={100}
      onClose={onClose}
      onMouseDown={onMouseDown}
      order={order}
      source={source}
      startPosition={{ right: 10, top: 80 }}
      title={
        showMapId
          ? `${t('feature_info_marker_info')} ${mapId}`
          : t('feature_info_marker_info')
      }
    >
      <Box sx={{ padding: 0 }}>
        {!!isOpen && !!hasLayers && !!mapPinLocation && (
          <Box
            data-testid="GetFeatureInfoList"
            ref={stopWheelEventPropagationRef}
          >
            {layerToUpdateList.map((layer) => {
              const { layerId, title, url } = layer;
              const result = gfiResultMap.get(layerId);
              if (!result) {
                return null;
              }
              return (
                <Grid
                  key={layerId}
                  sx={{
                    margin: '4px 6px 4px 6px',
                    padding: '6px',
                    backgroundColor: 'geowebColors.cards.cardContainer',
                    border: 'solid 1px',
                    borderColor: 'geowebColors.cards.cardContainerBorder',
                  }}
                >
                  <Typography variant="subtitle1">
                    {title}&nbsp;
                    <CustomIconButton
                      aria-label="Open layer featureinfo in another page"
                      onClick={(): void => {
                        window.open(url);
                      }}
                      sx={{ float: 'right' }}
                    >
                      <ExitDomain />
                    </CustomIconButton>
                  </Typography>

                  {!result.isLoading ? (
                    <div
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={sanitizeHTML(result.data)}
                      data-testid={`layer-result-ready-${layerId}`}
                      style={{
                        fontSize: '11px',
                        padding: '6px',
                        margin: '0',
                        fontFamily: 'Roboto,Helvetica,Arial,sans-serif',
                        fontWeight: '400',
                        lineHeight: '1.75',
                        letterSpacing: '0.25px',
                      }}
                    />
                  ) : (
                    <div data-testid={`layer-result-loading-${layerId}`}>
                      <CircularProgress color="inherit" data-testid="spinner" />
                    </div>
                  )}
                </Grid>
              );
            })}
          </Box>
        )}
        {!!hasLayers && !mapPinLocation && (
          <Typography
            data-testid="click-on-map-for-info"
            sx={{ padding: '10px' }}
            variant="subtitle1"
          >
            {t('feature_info_title')}
          </Typography>
        )}
        {!hasLayers && (
          <Typography
            data-testid="NoLayers"
            sx={{ padding: '10px' }}
            variant="subtitle1"
          >
            {t('feature_info_no_layers')}
          </Typography>
        )}
      </Box>
    </ToolContainerDraggable>
  );
};

export default GetFeatureInfoDialog;
